import { createCheckoutPaymentId } from 'checkout/lib/checkoutPaymentId'
import { formatMoneyInput, splitMoneyEvenly, type InlineSplitCardLeg } from 'checkout/lib/splitPaymentAllocation'
import { formatCardBrand } from 'lib/payment/formatCardBrand'
import { getSavedCardPaymentMethodId } from 'lib/payment/paymentUtils'

export const INLINE_SPLIT_NEW_CARD_SOURCE = 'new-card'
export const INLINE_SPLIT_UNSELECTED_SOURCE = ''
export const INLINE_SPLIT_PRIMARY_CARD_ID = 'card-1'

export function applyEqualSplitAmounts(
  legs: Array<InlineSplitCardLeg>,
  paymentAmount: number,
  currencyCode: string,
): Array<InlineSplitCardLeg> {
  const amounts = splitMoneyEvenly(paymentAmount, legs.length, currencyCode)
  return legs.map((leg, index) => ({
    ...leg,
    amount: formatMoneyInput(amounts[index] ?? 0, currencyCode),
  }))
}

export function createInlineSplitCardLeg(id: string, source: string, amount: string = ''): InlineSplitCardLeg {
  return {
    id,
    checkoutPaymentId: createCheckoutPaymentId(),
    amount,
    source,
  }
}

export function createNextCardId(existingLegs: Array<{ id: string }>): string {
  const highestNumber = existingLegs.reduce((highest, leg) => {
    const match = /^card-(\d+)$/.exec(leg.id)
    if (!match) return highest
    return Math.max(highest, Number(match[1]))
  }, 0)

  return `card-${highestNumber + 1}`
}

export function createInitialInlineSplitLegs(selectedCardId?: string, hasSavedCards: boolean = true): Array<InlineSplitCardLeg> {
  const firstSource = !selectedCardId || selectedCardId === INLINE_SPLIT_NEW_CARD_SOURCE
    ? INLINE_SPLIT_NEW_CARD_SOURCE
    : selectedCardId
  const secondSource = hasSavedCards ? INLINE_SPLIT_UNSELECTED_SOURCE : INLINE_SPLIT_NEW_CARD_SOURCE

  return [
    createInlineSplitCardLeg(INLINE_SPLIT_PRIMARY_CARD_ID, firstSource),
    createInlineSplitCardLeg('card-2', secondSource),
  ]
}

interface SplitCardSourceLeg {
  id: string
  source: string
}

export function formatSavedCardOptionLabel(card: App.CheckoutStripePaymentCard): string {
  return `${formatCardBrand(card.brand)} •••• ${card.last4}`
}

export function getUsedSavedCardSources(legs: Array<SplitCardSourceLeg>, exceptLegId?: string): Set<string> {
  return new Set(
    legs
      .filter(leg => (
        leg.id !== exceptLegId &&
        !!leg.source &&
        leg.source !== INLINE_SPLIT_NEW_CARD_SOURCE &&
        leg.source !== INLINE_SPLIT_UNSELECTED_SOURCE
      ))
      .map(leg => leg.source),
  )
}

export function getAvailableSavedCardsForLeg(
  leg: SplitCardSourceLeg,
  legs: Array<SplitCardSourceLeg>,
  savedCards: Array<App.CheckoutStripePaymentCard>,
): Array<App.CheckoutStripePaymentCard> {
  const usedSources = getUsedSavedCardSources(legs, leg.id)
  return savedCards.filter(card => {
    const cardId = getSavedCardPaymentMethodId(card)
    return cardId === leg.source || !usedSources.has(cardId)
  })
}

export function getSplitCardPickerState(
  leg: SplitCardSourceLeg,
  legs: Array<SplitCardSourceLeg>,
  savedCards: Array<App.CheckoutStripePaymentCard>,
  isActive: boolean,
) {
  const availableSavedCards = getAvailableSavedCardsForLeg(leg, legs, savedCards)
  const showSourcePicker = isActive && availableSavedCards.length > 0
  let source = INLINE_SPLIT_NEW_CARD_SOURCE
  if (showSourcePicker || leg.source !== INLINE_SPLIT_UNSELECTED_SOURCE) {
    source = leg.source
  }

  return {
    availableSavedCards,
    showSourcePicker,
    source,
  }
}
